import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './HeadlineCreateModal.scss';
import { categoryVisualMap } from '../CreateListing/categoryVisualMap';
import { getAccessToken } from "../../awsAuth";

import shareIcon from './img/share.png';
import locationIcon from './img/location.png';
import colorIcon from './img/color.png';

import categoryPng from './img/category.png';
import photoPng from './img/photo.png';
import locationPng from './img/location.png';

const CVANTO = { text: '#201F24' };
const HEADLINE_MIN_LENGTH = 6;

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  'http://localhost:5000';

function useHeadlineGradient(seed) {
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const r = (n) =>
    ((h = (h ^ (h << 13)) >>> 0),
    (h = (h ^ (h >>> 17)) >>> 0),
    (h = (h ^ (h << 5)) >>> 0),
    h & 0xfffffff) % n;

  const x1 = 55 + r(25);
  const y1 = 70 + r(20);
  const x2 = 22 + r(28);
  const y2 = 86 + r(10);

  const a1 = 0.16 + r(18) / 100;
  const a2 = 0.08 + r(12) / 100;

  const layer1 = `radial-gradient(120% 90% at ${x1}% ${y1}%, rgba(65,118,252,${a1}) 0%, rgba(65,118,252,0.06) 36%, rgba(65,118,252,0) 70%)`;
  const layer2 = `radial-gradient(80% 70% at ${x2}% ${y2}%, rgba(32,31,36,${a2}) 0%, rgba(32,31,36,0.05) 38%, rgba(32,31,36,0) 72%)`;
  return `${layer1}, ${layer2}`;
}

async function defaultSubmitToApi(form, photoFile, design, tags) {
  const token = getAccessToken();

  if (!token) {
    throw new Error('Немає токена авторизації');
  }

  const fd = new FormData();

  fd.append('title', String(form?.title || '').trim());
  fd.append('category', String(form?.category || '').trim());
  fd.append('citySlug', String(form?.citySlug || '').trim());
  fd.append('hasPhoto', String(!!photoFile));

  try {
    fd.append('tags', JSON.stringify(Array.isArray(tags) ? tags : []));
  } catch {
    fd.append('tags', '[]');
  }

  if (design && typeof design === 'object') {
    try {
      fd.append('design', JSON.stringify(design));
    } catch {}
  }

  if (photoFile) fd.append('photo', photoFile);

  const res = await fetch(`${API_BASE}/api/headlines`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: fd,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.message || data?.error || 'Не вдалося опублікувати headline';
    throw new Error(msg);
  }
  return data;
}

/* ===== Color helpers ===== */
function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

function hsvToRgb(h, s, v) {
  const c = v * s;
  const hh = (h % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0, g = 0, b = 0;

  if (hh >= 0 && hh < 1) { r = c; g = x; b = 0; }
  else if (hh >= 1 && hh < 2) { r = x; g = c; b = 0; }
  else if (hh >= 2 && hh < 3) { r = 0; g = c; b = x; }
  else if (hh >= 3 && hh < 4) { r = 0; g = x; b = c; }
  else if (hh >= 4 && hh < 5) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const m = v - c;
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

function rgbToHex(r, g, b) {
  const to = (x) => x.toString(16).padStart(2, '0');
  return `#${to(clamp(r,0,255))}${to(clamp(g,0,255))}${to(clamp(b,0,255))}`;
}

function hsvToHex(h, s, v) {
  const { r, g, b } = hsvToRgb(h, s, v);
  return rgbToHex(r, g, b);
}

function hexToRgb(hex) {
  const s = String(hex || '').trim();
  const m = s.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const v = m[1];
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

function rgbToHsv(r, g, b) {
  const rr = r / 255, gg = g / 255, bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const d = max - min;

  let h = 0;
  if (d === 0) h = 0;
  else if (max === rr) h = ((gg - bb) / d) % 6;
  else if (max === gg) h = (bb - rr) / d + 2;
  else h = (rr - gg) / d + 4;

  h = Math.round(h * 60);
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : d / max;
  const v = max;

  return { h, s, v };
}

function pickCityLineFromNominatim(data) {
  const addr = data?.address || {};

  const city =
    addr.city ||
    addr.town ||
    addr.village ||
    addr.hamlet ||
    addr.municipality ||
    '';

  const state =
    addr.state ||
    addr.region ||
    addr.state_district ||
    '';

  const country = addr.country || '';

  const cityClean = String(city || '').trim();
  const stateClean = String(state || '').trim();

  if (cityClean && stateClean && cityClean.toLowerCase() !== stateClean.toLowerCase()) {
    return `${cityClean}, ${stateClean}`;
  }

  if (cityClean) return cityClean;
  if (stateClean) return stateClean;

  const county = String(addr.county || '').trim();
  if (county) return county;

  const display = String(data?.display_name || '').trim();
  if (display) {
    const parts = display.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) return `${parts[0]}, ${parts[1]}`;
    return parts[0] || '';
  }

  return country ? String(country).trim() : '';
}

/* ===== Color picker (pointer events) ===== */
function ColorWheel({ value, onChange, onClose }) {
  const wheelRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [hs, setHs] = useState(() => ({ h: 210, s: 0.85, v: 1 }));

  useEffect(() => {
    const onDocDown = (e) => {
      const el = wheelRef.current?.closest?.('.hlnc__colorCard');
      if (!el) return;
      if (el.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, [onClose]);

  useEffect(() => {
    const rgb = hexToRgb(value);
    if (!rgb) return;
    const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
    const next = { h: hsv.h, s: clamp(hsv.s, 0, 1), v: 1 };
    setHs(next);
  }, [value]);

  const updateFromPoint = (clientX, clientY) => {
    const el = wheelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;

    const dx = clientX - cx;
    const dy = clientY - cy;

    const ang = Math.atan2(dy, dx);
    let h = (ang * 180) / Math.PI;
    h = (h + 90 + 360) % 360;

    const dist = Math.sqrt(dx * dx + dy * dy);
    const radius = r.width / 2;
    const s = clamp(dist / radius, 0, 1);

    const next = { h, s, v: 1 };
    setHs(next);

    const hex = hsvToHex(next.h, next.s, next.v);
    onChange?.(hex);
  };

  const onPointerDown = (e) => {
    e.preventDefault?.();
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    setDragging(true);
    updateFromPoint(e.clientX, e.clientY);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    e.preventDefault?.();
    updateFromPoint(e.clientX, e.clientY);
  };

  const onPointerUp = () => setDragging(false);

  const dotStyle = (() => {
    const el = wheelRef.current;
    const size = el?.getBoundingClientRect?.().width || 186;
    const radius = size / 2;
    const rr = hs.s * radius;

    const a = ((hs.h - 90) * Math.PI) / 180;
    const x = Math.cos(a) * rr;
    const y = Math.sin(a) * rr;
    return { transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` };
  })();

  return (
    <div className="hlnc__colorPopover" role="dialog" aria-label="Вибір кольору">
      <div className="hlnc__colorCard">
        <div
          ref={wheelRef}
          className="hlnc__colorWheel"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="hlnc__colorDot" style={dotStyle} />
        </div>

        <div className="hlnc__colorRow">
          <div className="hlnc__colorSwatch" style={{ background: value || '#ffffff' }} />
          <div className="hlnc__colorHex">{String(value || '').toUpperCase()}</div>
          <button
            type="button"
            className="hlnc__colorClose"
            onClick={onClose}
            aria-label="Закрити вибір кольору"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HeadlineCreateModal({
  avatarUrl,
  form,
  setForm,
  posting,
  postErr,
  storyLimits,
  showUpgradeCta,
  onUpgradeClick,
  onSubmit,
  onClose,
}) {
  const seed = useMemo(() => form?.title || 'create', [form?.title]);
  const gradient = useHeadlineGradient(seed);

  useEffect(() => {
  const body = document.body;
  const html = document.documentElement;
  const scrollY = window.scrollY || window.pageYOffset || 0;

  const prevBodyOverflow = body.style.overflow;
  const prevBodyTouchAction = body.style.touchAction;
  const prevBodyPosition = body.style.position;
  const prevBodyTop = body.style.top;
  const prevBodyLeft = body.style.left;
  const prevBodyRight = body.style.right;
  const prevBodyWidth = body.style.width;

  const prevHtmlOverflow = html.style.overflow;
  const prevHtmlOverscroll = html.style.overscrollBehavior;
  const prevBodyOverscroll = body.style.overscrollBehavior;

  body.style.overflow = 'hidden';
  body.style.touchAction = 'none';
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';

  html.style.overflow = 'hidden';
  html.style.overscrollBehavior = 'none';
  body.style.overscrollBehavior = 'none';

  return () => {
    body.style.overflow = prevBodyOverflow || '';
    body.style.touchAction = prevBodyTouchAction || '';
    body.style.position = prevBodyPosition || '';
    body.style.top = prevBodyTop || '';
    body.style.left = prevBodyLeft || '';
    body.style.right = prevBodyRight || '';
    body.style.width = prevBodyWidth || '';

    html.style.overflow = prevHtmlOverflow || '';
    html.style.overscrollBehavior = prevHtmlOverscroll || '';
    body.style.overscrollBehavior = prevBodyOverscroll || '';

    const restoreY = Math.abs(parseInt(body.style.top || `-${scrollY}`, 10)) || scrollY;
    window.scrollTo(0, restoreY);
  };
}, []);

  const [closing, setClosing] = useState(false);
  const doCloseAnimated = () => {
    setClosing(true);
    setTimeout(() => onClose?.(), 220);
  };

  const [openCat, setOpenCat] = useState(false);
  const catBtnRef = useRef(null);
  const ddRef = useRef(null);
  const [ddPos, setDdPos] = useState({ top: -9999, left: -9999, width: 320 });
  const [categoryError, setCategoryError] = useState(false);
  const [titleError, setTitleError] = useState('');
  const [isDraggingText, setIsDraggingText] = useState(false);

  const recalcDropdownPos = () => {
    const btn = catBtnRef.current;
    if (!btn) return null;
    const r = btn.getBoundingClientRect();
    const width = Math.min(Math.max(r.width, 260), Math.min(360, window.innerWidth - 16));
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    const top = Math.min(r.bottom + 8, window.innerHeight - 8);
    return { top, left, width };
  };

  const toggleCategories = () => {
    if (uiLockedRef.current) return;
    if (!openCat) {
      const pos = recalcDropdownPos();
      if (pos) setDdPos(pos);
      setOpenCat(true);
      setCategoryError(false);
    } else {
      setOpenCat(false);
    }
  };

  useEffect(() => {
    if (!openCat) return;

    const onScrollOrResize = () => {
      const pos = recalcDropdownPos();
      if (pos) setDdPos(pos);
    };

    const onDocDown = (e) => {
      const ddEl = ddRef.current;
      const btnEl = catBtnRef.current;
      if (!ddEl) return;
      if (ddEl.contains(e.target) || btnEl?.contains(e.target)) return;
      setOpenCat(false);
      setCategoryError(false);
    };

    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown);

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, [openCat]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (openCat) setOpenCat(false);
        else if (!uiLockedRef.current) doCloseAnimated();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openCat]);

  const taRef = useRef(null);
  const [fontPx, setFontPx] = useState(26);
  const MIN_FPX = 18;
  const MAX_FPX = 26;

  const baseTaHRef = useRef(0);
  const [growUpShiftY, setGrowUpShiftY] = useState(0);

  const autosizeTextarea = () => {
    const el = taRef.current;
    if (!el) return;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;

    const h = el.scrollHeight || 0;
    if (!baseTaHRef.current) baseTaHRef.current = h;

    const delta = Math.max(0, h - baseTaHRef.current);
    setGrowUpShiftY(delta / 2);
  };

  const adjustFont = () => {
    const el = taRef.current;
    if (!el) return;

    let size = MAX_FPX;
    el.style.fontSize = `${size}px`;

    autosizeTextarea();

    while (size > MIN_FPX) {
      if (el.scrollHeight <= el.clientHeight + 2) break;
      size -= 1;
      el.style.fontSize = `${size}px`;
      autosizeTextarea();
    }

    setFontPx(size);
  };

  useEffect(() => {
    adjustFont();
  }, [form?.title]);

  useEffect(() => {
    const onResize = () => adjustFont();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onVVChange = () => {
      if (document.activeElement === taRef.current) {
        keepViewportStill();
      }
    };

    vv.addEventListener('scroll', onVVChange);
    vv.addEventListener('resize', onVVChange);

    return () => {
      vv.removeEventListener('scroll', onVVChange);
      vv.removeEventListener('resize', onVVChange);
      cancelAnimationFrame(restoreScrollRafRef.current);
    };
  }, []);

  const categorySelected = form?.category && categoryVisualMap[form.category];

  useEffect(() => {
    const len = String(form?.title || '').trim().length;
    if (len > 0) setTitleError('');
  }, [form?.title]);

  // ===== Photo =====
  const fileInputRef = useRef(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  useEffect(() => {
    return () => {
      try {
        if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
      } catch {}
    };
  }, [photoPreview]);

  const pickPhoto = () => {
    if (uiLockedRef.current) return;
    fileInputRef.current?.click();
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!/^image\/(jpeg|png|webp|gif)$/.test(f.type)) {
      try { e.target.value = ''; } catch {}
      return;
    }

    const maxMb = 8;
    if (f.size > maxMb * 1024 * 1024) {
      try { e.target.value = ''; } catch {}
      return;
    }

    try {
      if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    } catch {}

    const url = URL.createObjectURL(f);
    setPhotoFile(f);
    setPhotoPreview(url);

    setForm?.({ ...form, hasPhoto: true });
  };

  const removePhoto = () => {
    if (uiLockedRef.current) return;
    try {
      if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    } catch {}
    setPhotoFile(null);
    setPhotoPreview('');
    try {
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {}
    setForm?.({ ...form, hasPhoto: false });
  };

  // ===== Location =====
  const [locBusy, setLocBusy] = useState(false);
  const [locErr, setLocErr] = useState('');

  const reverseGeocode = async (lat, lng) => {
    const url =
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
      `&lat=${encodeURIComponent(lat)}` +
      `&lon=${encodeURIComponent(lng)}` +
      `&zoom=12&addressdetails=1` +
      `&accept-language=uk`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Accept-Language': 'uk,uk-UA;q=0.9,en;q=0.4',
      },
    });

    if (!res.ok) throw new Error('REVERSE_FAILED');

    const data = await res.json();
    return pickCityLineFromNominatim(data);
  };

  const useMyLocation = async () => {
    if (uiLockedRef.current) return;
    if (locBusy) return;
    setLocBusy(true);
    setLocErr('');

    try {
      if (!navigator.geolocation) throw new Error('NO_GEO');

      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 30000,
        });
      });

      const lat = pos?.coords?.latitude;
      const lng = pos?.coords?.longitude;

      if (typeof lat !== 'number' || typeof lng !== 'number') throw new Error('BAD_COORDS');

      let city = '';
      try {
        city = await reverseGeocode(lat, lng);
      } catch {
        city = '';
      }

      if (!city) {
        setLocErr('Не вдалося визначити місто. Введіть вручну.');
        return;
      }

      setForm?.({ ...form, citySlug: city });
    } catch (e) {
      const code = e?.code;
      if (code === 1) setLocErr('Доступ до геолокації заборонено');
      else if (code === 2) setLocErr('Не вдалося визначити локацію');
      else if (code === 3) setLocErr('Таймаут геолокації');
      else setLocErr('Не вдалося підтягнути локацію');
    } finally {
      setLocBusy(false);
    }
  };

  const canPublish = true;

  const cardRef = useRef(null);
  const centerRef = useRef(null);

  const [textSelected, setTextSelected] = useState(false);
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [showColorPicker, setShowColorPicker] = useState(false);

  const colorBtnRef = useRef(null);
  const colorCloseTimerRef = useRef(null);

  const [colorPickerMounted, setColorPickerMounted] = useState(false);
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [colorPickerPos, setColorPickerPos] = useState({
    top: -9999,
    left: -9999,
  });

  const [textPos, setTextPos] = useState({ x: 0, y: 0 });
  const [textScale, setTextScale] = useState(1);
  const [textBoxSize, setTextBoxSize] = useState({ w: 320, h: 140 });

  useEffect(() => {
    requestAnimationFrame(() => {
      autosizeTextarea();
      adjustFont();
    });
  }, [textBoxSize.w, textBoxSize.h]);

  const [kbShiftY, setKbShiftY] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const initialPosAppliedRef = useRef(false);

  const lockScrollYRef = useRef(0);
  const restoreScrollRafRef = useRef(0);

  const dismissKeyboard = () => {
    try {
      if (document.activeElement === taRef.current) {
        taRef.current?.blur?.();
      }
    } catch {}
  };

  const getSafeInsetPx = (name) => {
    try {
      const raw = getComputedStyle(cardRef.current || document.documentElement)
        .getPropertyValue(name)
        .trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  const recalcColorPickerPos = () => {
    const POP_W = 210;
    const POP_H = 250;

    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;

    const safeTop = getSafeInsetPx('--hlnc-safe-t');
    const safeRight = getSafeInsetPx('--hlnc-safe-r');
    const safeBottom = getSafeInsetPx('--hlnc-safe-b');

    const isMobile = window.matchMedia?.('(max-width: 768px)')?.matches;

    if (isMobile) {
      const railOffset = 74 + safeRight;
      const left = Math.max(12, vw - railOffset - POP_W - 14);

      const publishBottom = 100 + safeBottom;
      const maxTop = vh - publishBottom - POP_H - 16;
      const top = Math.min(
        Math.max(112 + safeTop, 96),
        Math.max(16, maxTop)
      );

      setColorPickerPos({ top, left });
      return;
    }

    const btn = colorBtnRef.current;
    if (btn) {
      const r = btn.getBoundingClientRect();

      let left = r.right - POP_W;
      let top = r.top - POP_H - 14;

      if (left < 12) left = 12;
      if (left + POP_W > vw - 12) left = vw - POP_W - 12;

      const publishBottom = 24 + safeBottom;
      const maxTop = vh - publishBottom - POP_H - 12;
      if (top > maxTop) top = maxTop;
      if (top < 12) top = 12;

      setColorPickerPos({ top, left });
      return;
    }

    setColorPickerPos({
      top: Math.max(16, 96 + safeTop),
      left: Math.max(12, vw - POP_W - 96 - safeRight),
    });
  };

  const openColorPicker = () => {
    clearTimeout(colorCloseTimerRef.current);
    recalcColorPickerPos();
    setColorPickerMounted(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setColorPickerVisible(true);
        setShowColorPicker(true);
      });
    });
  };

  const closeColorPicker = () => {
    clearTimeout(colorCloseTimerRef.current);
    setColorPickerVisible(false);
    setShowColorPicker(false);

    colorCloseTimerRef.current = setTimeout(() => {
      setColorPickerMounted(false);
    }, 220);
  };

  const toggleColorPicker = () => {
    if (uiLockedRef.current) return;

    if (colorPickerMounted || showColorPicker) {
      closeColorPicker();
    } else {
      openColorPicker();
    }
  };

  const [showLocPopover, setShowLocPopover] = useState(false);
  const locPopRef = useRef(null);

  useEffect(() => {
    const boxEl = document.getElementById('hlnc-textbox');
    if (!boxEl) return;
    const w = boxEl.offsetWidth || 320;
    const h = boxEl.offsetHeight || 140;
    setTextBoxSize({ w, h });
  }, []);

  useEffect(() => {
    if (initialPosAppliedRef.current) return;

    const isMobile = window.matchMedia?.('(max-width: 768px)')?.matches;
    if (isMobile) {
      setTextPos((p) => {
        if (p && (p.x !== 0 || p.y !== 0)) return p;
        return { x: 0, y: -12 };
      });
    }

    initialPosAppliedRef.current = true;
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    const cardEl = cardRef.current;
    if (!cardEl) return;

    let raf = 0;

    const recalc = () => {
      cancelAnimationFrame(raf);

      raf = requestAnimationFrame(() => {
        const layoutH =
          window.innerHeight ||
          document.documentElement?.clientHeight ||
          0;

        const vvH = vv?.height || layoutH;
        const vvTop = vv?.offsetTop || 0;
        const visibleBottom = vvTop + vvH;

        const occludedBottom = Math.max(0, layoutH - visibleBottom);

        const isTaFocused = document.activeElement === taRef.current;
        const KB_OPEN_THRESHOLD = 120;
        const keyboardOpen = isTaFocused && occludedBottom >= KB_OPEN_THRESHOLD;

        setKbShiftY(0);

        cardEl.style.setProperty('--hlnc-browser-bar-b', `${keyboardOpen ? 0 : clamp(occludedBottom, 0, 140)}px`);
        cardEl.style.setProperty('--hlnc-keyboard-open', keyboardOpen ? '1' : '0');
        cardEl.style.setProperty('--hlnc-keyboard-b', `${keyboardOpen ? occludedBottom : 0}px`);

        cardEl.style.setProperty('--hlnc-vv-h', `100lvh`);
      });
    };

    const onFocusIn = () => recalc();
    const onFocusOut = () => {
      setTimeout(recalc, 40);
    };

    if (vv) {
      vv.addEventListener('resize', recalc);
      vv.addEventListener('scroll', recalc);
    }

    window.addEventListener('resize', recalc);
    window.addEventListener('orientationchange', recalc);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    recalc();

    return () => {
      if (vv) {
        vv.removeEventListener('resize', recalc);
        vv.removeEventListener('scroll', recalc);
      }
      window.removeEventListener('resize', recalc);
      window.removeEventListener('orientationchange', recalc);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      cancelAnimationFrame(raf);
    };
  }, []);

  const dragRef = useRef({
    dragging: false,
    maybeDrag: false,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
    moved: false,
  });

  const pointersRef = useRef(new Map());
  const pinchRef = useRef({
    pinching: false,
    startDist: 0,
    baseScale: 1,
  });

  const getDist = (a, b) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const onBackdropClickSelectOff = () => {
    if (uiLockedRef.current) return;
    setTextSelected(false);
    closeColorPicker();
    setShowLocPopover(false);
  };

  const onTextSelect = (e) => {
    if (uiLockedRef.current) return;
    e?.stopPropagation?.();
    setTextSelected(true);
  };

  const onTextBoxPointerDown = (e) => {
  if (uiLockedRef.current) return;
  if (!textSelected) return;

  e.stopPropagation();
  e.preventDefault?.();

  try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      pinchRef.current.pinching = true;
      pinchRef.current.startDist = getDist(pts[0], pts[1]);
      pinchRef.current.baseScale = textScale;
      dragRef.current.dragging = false;
      dragRef.current.maybeDrag = false;
      return;
    }

    dragRef.current.maybeDrag = true;
    dragRef.current.dragging = false;
    dragRef.current.moved = false;

    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.baseX = textPos.x;
    dragRef.current.baseY = textPos.y;
  };

  const onTextBoxPointerMove = (e) => {
    if (uiLockedRef.current) return;
    if (!textSelected) return;

    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (pinchRef.current.pinching && pointersRef.current.size >= 2) {
      const pts = Array.from(pointersRef.current.values()).slice(0, 2);
      const dist = getDist(pts[0], pts[1]);
      const k = pinchRef.current.startDist ? dist / pinchRef.current.startDist : 1;

      const nextScale = clamp(pinchRef.current.baseScale * k, 0.6, 2.4);
      setTextScale(nextScale);
      setIsDraggingText(true);

      e.stopPropagation();
      e.preventDefault?.();
      return;
    }

    if (!dragRef.current.maybeDrag && !dragRef.current.dragging) return;

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    const TH = 6;
    if (!dragRef.current.dragging) {
    if (Math.abs(dx) + Math.abs(dy) < TH) return;

    dragRef.current.dragging = true;
    dragRef.current.moved = true;
    setIsDraggingText(true);

    try { taRef.current?.blur?.(); } catch {}
    e.preventDefault?.();
  }

    setTextPos({
      x: dragRef.current.baseX + dx,
      y: dragRef.current.baseY + dy,
    });

    e.preventDefault?.();
  };

  const onTextBoxPointerUp = (e) => {
  if (pointersRef.current.has(e.pointerId)) {
    pointersRef.current.delete(e.pointerId);
  }

  if (pinchRef.current.pinching) {
    if (pointersRef.current.size < 2) {
      pinchRef.current.pinching = false;
      pinchRef.current.startDist = 0;
    }
  }

  dragRef.current.dragging = false;
  dragRef.current.maybeDrag = false;
  setIsDraggingText(false);
};

  useEffect(() => {
    const el = centerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (uiLockedRef.current) return;
      if (!textSelected) return;
      if (!e.ctrlKey) return;
      e.preventDefault();

      const delta = e.deltaY;
      const step = delta > 0 ? -0.06 : 0.06;
      setTextScale((s) => clamp(s + step, 0.6, 2.4));
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [textSelected]);

  const scaleHandleRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    baseScale: 1,
  });

  const onScaleHandleDown = (e) => {
    if (uiLockedRef.current) return;
    if (!textSelected) return;
    e.stopPropagation();
    e.preventDefault?.();

    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    scaleHandleRef.current.active = true;
    scaleHandleRef.current.startX = e.clientX;
    scaleHandleRef.current.startY = e.clientY;
    scaleHandleRef.current.baseScale = textScale;
  };

  const onScaleHandleMove = (e) => {
    if (uiLockedRef.current) return;
    if (!scaleHandleRef.current.active) return;
    e.preventDefault?.();

    const dx = e.clientX - scaleHandleRef.current.startX;
    const dy = e.clientY - scaleHandleRef.current.startY;

    const dist = (dx + dy) / 220;
    const next = clamp(scaleHandleRef.current.baseScale + dist, 0.6, 2.4);
    setTextScale(next);
  };

  const onScaleHandleUp = () => {
    scaleHandleRef.current.active = false;
  };

  const resizeRef = useRef({
    active: false,
    dir: null,
    startX: 0,
    startY: 0,
    baseW: 320,
    baseH: 140,
  });

  const MIN_W = 160;
  const MAX_W = 520;
  const MIN_H = 60;
  const MAX_H = 320;

  const onResizeHandleDown = (dir, e) => {
    if (uiLockedRef.current) return;
    if (!textSelected) return;

    e.stopPropagation();
    e.preventDefault?.();

    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}

    resizeRef.current.active = true;
    resizeRef.current.dir = dir;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.baseW = textBoxSize.w;
    resizeRef.current.baseH = textBoxSize.h;
  };

  const onResizeHandleMove = (e) => {
    if (!resizeRef.current.active) return;
    e.preventDefault?.();

    const dx = e.clientX - resizeRef.current.startX;
    const dy = e.clientY - resizeRef.current.startY;

    const dir = resizeRef.current.dir;

    setTextBoxSize((prev) => {
      let w = prev.w;
      let h = prev.h;

      if (dir === 'l') w = clamp(resizeRef.current.baseW - dx, MIN_W, MAX_W);
      if (dir === 'r') w = clamp(resizeRef.current.baseW + dx, MIN_W, MAX_W);

      if (dir === 't') h = clamp(resizeRef.current.baseH - dy, MIN_H, MAX_H);
      if (dir === 'b') h = clamp(resizeRef.current.baseH + dy, MIN_H, MAX_H);

      return { w, h };
    });
  };

  const onResizeHandleUp = () => {
    resizeRef.current.active = false;
    resizeRef.current.dir = null;
  };

  useEffect(() => {
    const onDocDown = (e) => {
      if (uiLockedRef.current) return;

      const box = document.getElementById('hlnc-textbox');
      const pop = document.getElementById('hlnc-color-pop');
      const locPop = locPopRef.current;

      if (box && box.contains(e.target)) return;
      if (pop && pop.contains(e.target)) return;
      if (locPop && locPop.contains(e.target)) return;

      setTextSelected(false);
      closeColorPicker();
      setShowLocPopover(false);
    };

    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown, { passive: true });

    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, []);

  useEffect(() => {
    if (!colorPickerMounted) return;

    const onRecalc = () => recalcColorPickerPos();

    window.addEventListener('resize', onRecalc);
    window.addEventListener('scroll', onRecalc, true);
    window.visualViewport?.addEventListener?.('resize', onRecalc);
    window.visualViewport?.addEventListener?.('scroll', onRecalc);

    onRecalc();

    return () => {
      window.removeEventListener('resize', onRecalc);
      window.removeEventListener('scroll', onRecalc, true);
      window.visualViewport?.removeEventListener?.('resize', onRecalc);
      window.visualViewport?.removeEventListener?.('scroll', onRecalc);
    };
  }, [colorPickerMounted]);

  useEffect(() => {
    return () => {
      clearTimeout(colorCloseTimerRef.current);
    };
  }, []);

  const keepViewportStill = () => {
    const y = lockScrollYRef.current || window.scrollY || window.pageYOffset || 0;

    try {
      window.scrollTo(0, y);
    } catch {}

    cancelAnimationFrame(restoreScrollRafRef.current);

    restoreScrollRafRef.current = requestAnimationFrame(() => {
      try { window.scrollTo(0, y); } catch {}
      restoreScrollRafRef.current = requestAnimationFrame(() => {
        try { window.scrollTo(0, y); } catch {}
      });
    });
  };

  const computedFont = `${Math.max(12, Math.round(fontPx))}px`;
  const clampN = (v, a, b, f) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return f;
    return Math.max(a, Math.min(b, n));
  };

  const buildDesignPayload = () => {
    const center = centerRef.current;
    const boxEl = document.getElementById('hlnc-textbox');

    const rect = center?.getBoundingClientRect?.();
    if (!rect || !rect.width || !rect.height) return null;

    const xPct = clampN(50 + (Number(textPos?.x || 0) / rect.width) * 100, 0, 100, 50);
    const yPct = clampN(50 + (Number(textPos?.y || 0) / rect.height) * 100, 0, 100, 40);

    const scale = clampN(textScale, 0.3, 4, 1);
    const color = String(textColor || '#FFFFFF');

    const boxW = clampN(textBoxSize?.w, 40, 1000, 320);
    const boxH = clampN(textBoxSize?.h, 30, 800, 140);

    return {
      v: 2,
      text: {
        xPct,
        yPct,
        scale,
        rotate: 0,
        color,
        boxW,
        boxH,
        fontPx,
      },
      textX: xPct,
      textY: yPct,
      textScale: scale,
      textColor: color,
      fontPx,
    };
  };

  // ===== Share progress ring =====
  const [shareProgress, setShareProgress] = useState(0);
  const [shareBusy, setShareBusy] = useState(false);
  const shareAnimRef = useRef({ raf: 0, phase: 'idle' });

  const uiLocked = shareBusy || posting;
  const uiLockedRef = useRef(false);
  useEffect(() => { uiLockedRef.current = uiLocked; }, [uiLocked]);

  const stopShareAnim = () => {
    try { cancelAnimationFrame(shareAnimRef.current.raf); } catch {}
    shareAnimRef.current.raf = 0;
    shareAnimRef.current.phase = 'idle';
  };

  const startShareAnim = () => {
    stopShareAnim();
    setShareProgress(0.06);
    shareAnimRef.current.phase = 'to90';

    const start = performance.now();
    const DUR = 1200;

    const tick = (t) => {
      const p = Math.min(1, (t - start) / DUR);
      const ease = 1 - Math.pow(1 - p, 3);
      const val = 0.06 + (0.90 - 0.06) * ease;
      setShareProgress(val);
      if (p < 1 && shareAnimRef.current.phase === 'to90') {
        shareAnimRef.current.raf = requestAnimationFrame(tick);
      }
    };

    shareAnimRef.current.raf = requestAnimationFrame(tick);
  };

  const holdNearDone = () => {
    stopShareAnim();
    shareAnimRef.current.phase = 'hold';

    let base = 0.92;
    let dir = 1;
    const tick = () => {
      base += dir * 0.002;
      if (base > 0.965) dir = -1;
      if (base < 0.92) dir = 1;
      setShareProgress((prev) => Math.max(prev, base));
      if (shareAnimRef.current.phase === 'hold') {
        shareAnimRef.current.raf = requestAnimationFrame(tick);
      }
    };

    shareAnimRef.current.raf = requestAnimationFrame(tick);
  };

  const finishTo100AndClose = () => {
    stopShareAnim();
    shareAnimRef.current.phase = 'to100';

    const from = Math.max(shareProgress, 0.94);
    const start = performance.now();
    const DUR = 320;

    const tick = (t) => {
      const p = Math.min(1, (t - start) / DUR);
      const ease = 1 - Math.pow(1 - p, 2);
      const val = from + (1 - from) * ease;
      setShareProgress(val);

      if (p < 1 && shareAnimRef.current.phase === 'to100') {
        shareAnimRef.current.raf = requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          setShareBusy(false);
          setShareProgress(0);
          doCloseAnimated();
        }, 120);
      }
    };

    shareAnimRef.current.raf = requestAnimationFrame(tick);
  };

  useEffect(() => {
    return () => stopShareAnim();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (posting || shareBusy) return;

    const currentTitle = String(form?.title || '').trim();
    const currentTitleLength = currentTitle.length;

    if (currentTitleLength === 0) {
      setTitleError('Headline повинен містити текст');
      try {
        taRef.current?.focus();
        document.getElementById('hlnc-textbox')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {}
      return;
    }

    if (currentTitleLength < HEADLINE_MIN_LENGTH) {
      setTitleError(`Заголовок мінімум ${HEADLINE_MIN_LENGTH} символів`);
      try {
        taRef.current?.focus();
        document.getElementById('hlnc-textbox')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {}
      return;
    }

    if (!form?.category) {
      setCategoryError(true);
      try {
        catBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {}
      return;
    }

    try {
      console.log('Submitting headline with photo:', !!photoFile, photoFile?.name, photoFile?.size);

      const design = buildDesignPayload();
      console.log('Headline design payload:', design);

      setShareBusy(true);
      startShareAnim();

      setTimeout(() => {
        if (shareAnimRef.current.phase === 'to90') holdNearDone();
      }, 1250);

      const tags = [];

      if (typeof onSubmit === 'function') {
        const res = await onSubmit?.(e, { photoFile, design, tags });
        if (res === false) {
          setShareBusy(false);
          setShareProgress(0);
          stopShareAnim();
          return;
        }
      } else {
        await defaultSubmitToApi(
          { ...form, title: currentTitle },
          photoFile,
          design,
          tags
        );
      }

      finishTo100AndClose();
    } catch (err) {
      console.error(err);
      setShareBusy(false);
      setShareProgress(0);
      stopShareAnim();
    }
  };

  const handleRightCategory = () => {
    if (uiLockedRef.current) return;
    toggleCategories();
  };

  const handleRightPhoto = () => {
    if (uiLockedRef.current) return;
    if (!photoPreview) pickPhoto();
    else removePhoto();
  };

  const handleRightLocation = () => {
    if (uiLockedRef.current) return;
    setShowLocPopover((v) => !v);
    setLocErr('');
  };

  const trimmedTitle = String(form?.title || '').trim();
  const titleLength = trimmedTitle.length;

  const hasCategory = !!(form?.category && categoryVisualMap[form.category]);
  const hasPhoto = !!photoPreview;
  const hasLocation = !!String(form?.citySlug || '').trim();

  const isTitleEmpty = titleLength === 0;
  const isTitleTooShort = titleLength > 0 && titleLength < HEADLINE_MIN_LENGTH;

  return (
    <div
      className={`hlnc__backdrop ${closing ? 'is-fadeout' : 'is-fadein'} ${isDraggingText ? 'is-dragging-text' : ''}`}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (uiLockedRef.current) return;

          const wasTyping = document.activeElement === taRef.current;

          dismissKeyboard();
          onBackdropClickSelectOff();

          if (!wasTyping) {
            doCloseAnimated();
          }
        }
      }}
    >
      <div
        ref={cardRef}
        className={`hlnc__card ${closing ? 'is-closing' : 'is-open'} ${photoPreview ? 'has-photo' : ''} ${isTyping ? 'is-typing' : ''} ${isDraggingText ? 'is-dragging-text' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hlnc__storyBg">
          {photoPreview ? (
            <>
              <img className="hlnc__storyImg" src={photoPreview} alt="" />
              <div className="hlnc__storyShade" />
            </>
          ) : null}

          <div className="hlnc__wave" style={{ backgroundImage: gradient }} />
          <div className="hlnc__storyFadeTop" />
          <div className="hlnc__storyFadeBottom" />
        </div>

        <div className="hlnc__topBar">
          <button
            type="button"
            className="hlnc__closeTop"
            aria-label="Закрити"
            onClick={() => {
              if (uiLockedRef.current) return;
              setTextSelected(false);
              closeColorPicker();
              setShowLocPopover(false);
              doCloseAnimated();
            }}
            disabled={uiLocked}
          >
            <span className="hlnc__closeGlyph" aria-hidden="true">×</span>
          </button>

          <div className="hlnc__topCenterStack">
            <div className="hlnc__topCounter">
              {(form.title || '').length}/100
            </div>

            {storyLimits ? (
              <div className="hlnc__topCounter hlnc__storiesCounter" title="Активних Stories">
                {storyLimits.active}/{storyLimits.limit} Stories
              </div>
            ) : null}
          </div>

          <button
            ref={catBtnRef}
            type="button"
            className="hlnc__catAnchor"
            onClick={toggleCategories}
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>

        <form
          className={`hlnc__center ${isDraggingText ? 'is-dragging-text' : ''}`}
          ref={centerRef}
          onSubmit={handleSubmit}
          onClick={(e) => {
            if (uiLockedRef.current) return;

            const box = document.getElementById('hlnc-textbox');
            const clickedInsideTextBox = box?.contains?.(e.target);

            if (!clickedInsideTextBox) {
              dismissKeyboard();
            }

            if (textSelected && !clickedInsideTextBox) {
              setTextSelected(false);
              closeColorPicker();
            }
          }}
        >
          <div
            id="hlnc-textbox"
            className={`hlnc__textBox ${textSelected ? 'is-selected' : ''} ${titleError ? 'is-error' : ''}`}
            style={{
              transform: `translate(-50%, -50%) translate(${textPos.x}px, ${textPos.y - growUpShiftY}px) scale(${textScale})`,
              width: `${textBoxSize.w}px`,
              minHeight: `${textBoxSize.h}px`,
              maxWidth: '92vw',
            }}
            onPointerDown={onTextBoxPointerDown}
            onPointerMove={onTextBoxPointerMove}
            onPointerUp={onTextBoxPointerUp}
            onPointerCancel={onTextBoxPointerUp}
            onClick={(e) => onTextSelect(e)}
          >
            {textSelected ? (
              <button
                ref={colorBtnRef}
                type="button"
                className="hlnc__colorBtn"
                onPointerDown={(e) => { e.stopPropagation(); }}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => {
                  if (uiLockedRef.current) return;
                  e.stopPropagation();
                  toggleColorPicker();
                }}
                aria-label="Вибрати колір тексту"
                title="Колір"
                disabled={uiLocked}
              >
                <img src={colorIcon} alt="" className="hlnc__icon" />
              </button>
            ) : null}

            {textSelected ? (
              <div
                className="hlnc__scaleHandle"
                role="button"
                aria-label="Змінити розмір тексту"
                tabIndex={0}
                onPointerDown={(e) => { e.stopPropagation(); onScaleHandleDown(e); }}
                onPointerMove={onScaleHandleMove}
                onPointerUp={onScaleHandleUp}
                onPointerCancel={onScaleHandleUp}
              />
            ) : null}

            {textSelected ? (
              <>
                <div
                  className="hlnc__resizeHandle hlnc__resizeHandle--l"
                  role="button"
                  aria-label="Розтягнути текст по ширині"
                  onPointerDown={(e) => onResizeHandleDown('l', e)}
                  onPointerMove={onResizeHandleMove}
                  onPointerUp={onResizeHandleUp}
                  onPointerCancel={onResizeHandleUp}
                />
                <div
                  className="hlnc__resizeHandle hlnc__resizeHandle--r"
                  role="button"
                  aria-label="Розтягнути текст по ширині"
                  onPointerDown={(e) => onResizeHandleDown('r', e)}
                  onPointerMove={onResizeHandleMove}
                  onPointerUp={onResizeHandleUp}
                  onPointerCancel={onResizeHandleUp}
                />
                <div
                  className="hlnc__resizeHandle hlnc__resizeHandle--t"
                  role="button"
                  aria-label="Змінити висоту блоку"
                  onPointerDown={(e) => onResizeHandleDown('t', e)}
                  onPointerMove={onResizeHandleMove}
                  onPointerUp={onResizeHandleUp}
                  onPointerCancel={onResizeHandleUp}
                />
                <div
                  className="hlnc__resizeHandle hlnc__resizeHandle--b"
                  role="button"
                  aria-label="Змінити висоту блоку"
                  onPointerDown={(e) => onResizeHandleDown('b', e)}
                  onPointerMove={onResizeHandleMove}
                  onPointerUp={onResizeHandleUp}
                  onPointerCancel={onResizeHandleUp}
                />
              </>
            ) : null}

            <textarea
              ref={taRef}
              maxLength={100}
              value={form.title}
              onChange={(e) => {
                if (uiLockedRef.current) return;
                setTitleError('');
                setForm({ ...form, title: e.target.value });
                requestAnimationFrame(() => autosizeTextarea());
              }}
              placeholder='Напр.: "Потрібен фотограф у Львові завтра"'
              className="hlnc__title-input"
              aria-label="Опис (до 100 символів)"
              rows={1}
              style={{ fontSize: computedFont, color: textColor }}
              required
              disabled={uiLocked}
              onFocus={() => {
                lockScrollYRef.current = window.scrollY || window.pageYOffset || 0;
                setIsTyping(true);

                setTimeout(() => {
                  keepViewportStill();
                }, 0);

                setTimeout(() => {
                  keepViewportStill();
                }, 80);
              }}
              onBlur={() => {
                setIsTyping(false);
                setTimeout(() => {
                  setKbShiftY(0);
                  keepViewportStill();
                }, 0);
              }}
              onClick={(e) => onTextSelect(e)}

              onPointerDown={(e) => {
                if (!textSelected) return;
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                if (!textSelected) return;
                e.preventDefault();
              }}
            />
          </div>

          <input
            ref={fileInputRef}
            className="hlnc__file"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onPickFile}
            disabled={uiLocked}
          />

          {(titleError || postErr) ? (
            <div className="hlnc__error" role="status">
              {titleError || postErr}
              {!titleError && postErr && showUpgradeCta ? (
                <button
                  type="button"
                  className="hlnc__upgradeLink"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onUpgradeClick?.();
                  }}
                >
                  Спробувати Cvanto Plus (до 5 Stories) →
                </button>
              ) : null}
            </div>
          ) : null}

          {shareBusy ? (
            <div className="hlnc__postingOverlay" aria-live="polite">
              <div className="hlnc__postingCard">
                <div className="hlnc__postingTitle">Зачекайте…</div>
                <div className="hlnc__postingSub">Публікуємо headline</div>
              </div>
            </div>
          ) : null}
        </form>

        <div className="hlnc__uiLayer" aria-hidden="false">
          <div className="hlnc__rightRail">
            <div className="hlnc__railItem">
              <button
                type="button"
                className={`hlnc__railBtn ${hasCategory ? 'is-done' : ''} ${categoryError && !hasCategory ? 'is-error' : ''}`}
                aria-label="Категорія"
                title="Категорія"
                onClick={handleRightCategory}
                disabled={uiLocked}
              >
                <img src={categoryPng} alt="" className="hlnc__icon" />
              </button>
              <div className="hlnc__railLabel">Категорія</div>
            </div>

            <div className="hlnc__railItem">
              <button
                type="button"
                className={`hlnc__railBtn ${hasPhoto ? 'is-done' : ''}`}
                aria-label={photoPreview ? 'Прибрати фото' : 'Додати фото'}
                title={photoPreview ? 'Прибрати фото' : 'Додати фото'}
                onClick={handleRightPhoto}
                disabled={uiLocked}
              >
                <img src={photoPng} alt="" className="hlnc__icon" />
              </button>
              <div className="hlnc__railLabel">Додати фото</div>
            </div>

            <div className="hlnc__railItem">
              <button
                type="button"
                className={`hlnc__railBtn ${locBusy ? 'is-busy' : ''} ${showLocPopover ? 'is-active' : ''} ${hasLocation ? 'is-done' : ''}`}
                aria-label="Ваше місто"
                title="Ваше місто"
                onClick={handleRightLocation}
                disabled={uiLocked}
              >
                <img src={locationPng} alt="" className="hlnc__icon" />
              </button>
              <div className="hlnc__railLabel">Ваше місто</div>
            </div>
          </div>

          {showLocPopover ? (
            <div
              ref={locPopRef}
              className="hlnc__locPopover"
              role="dialog"
              aria-label="Місто"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="hlnc__locTitle">Місто</div>

              <div className="hlnc__locRow">
                <input
                  type="text"
                  value={form.citySlug || ''}
                  onChange={(e) => {
                    if (uiLockedRef.current) return;
                    setForm({ ...form, citySlug: e.target.value });
                  }}
                  placeholder="місто (опц.)"
                  aria-label="Місто (опціонально)"
                  disabled={uiLocked}
                />

                <button
                  type="button"
                  className={`hlnc__locPick ${locBusy ? 'is-busy' : ''}`}
                  onClick={useMyLocation}
                  aria-label="Підтягнути мою локацію"
                  title="Підтягнути мою локацію"
                  disabled={uiLocked || locBusy}
                >
                  {locBusy ? (
                    <span className="hlnc__locSpinner" aria-hidden="true" />
                  ) : (
                    <img src={locationIcon} alt="" className="hlnc__icon" />
                  )}
                </button>
              </div>

              {locErr ? <div className="hlnc__locError">{locErr}</div> : null}

              <button
                type="button"
                className="hlnc__locDone"
                onClick={() => setShowLocPopover(false)}
                disabled={uiLocked}
              >
                Готово
              </button>
            </div>
          ) : null}

          <div className="hlnc__publishBar">
            <button
              type="submit"
              className={`hlnc__publishBtn ${!canPublish ? 'is-disabled' : ''}`}
              onClick={handleSubmit}
              disabled={uiLocked || !canPublish}
              aria-label="Опублікувати"
              style={{
                background: 'rgba(37, 99, 235, 0.95)',
              }}
            >
              <span className="hlnc__publishAvatar">
                <img src={avatarUrl} alt="" />
              </span>
              <span className="hlnc__publishText">Опублікувати</span>

              {shareBusy ? (
                <span
                  className="hlnc__publishProg"
                  style={{ transform: `scaleX(${clamp(shareProgress, 0, 1)})` }}
                  aria-hidden="true"
                />
              ) : null}
            </button>

            <div className="hlnc__fabs hlnc__fabs--legacy">
              <button
                type="button"
                className="hlnc__fab"
                aria-label="Закрити"
                onClick={() => {
                  if (uiLockedRef.current) return;
                  setTextSelected(false);
                  closeColorPicker();
                  doCloseAnimated();
                }}
                disabled={uiLocked}
              >
                <span className="hlnc__x" aria-hidden="true">×</span>
              </button>

              <div className={`hlnc__fabWrap ${shareBusy ? 'is-busy' : ''}`}>
                {shareBusy ? (
                  <svg className="hlnc__fabRing" viewBox="0 0 64 64" aria-hidden="true">
                    <circle className="hlnc__fabRingTrack" cx="32" cy="32" r="28" />
                    <circle
                      className="hlnc__fabRingProg"
                      cx="32"
                      cy="32"
                      r="28"
                      style={{ strokeDashoffset: `${(1 - clamp(shareProgress, 0, 1)) * (2 * Math.PI * 28)}` }}
                    />
                  </svg>
                ) : null}

                <button
                  type="submit"
                  className={`hlnc__fab hlnc__fab--primary ${!canPublish ? 'is-disabled' : ''}`}
                  aria-label="Опублікувати"
                  onClick={handleSubmit}
                  disabled={uiLocked || !canPublish}
                >
                  <img className="hlnc__shareIcon" src={shareIcon} alt="" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openCat &&
        createPortal(
          <div
            id="hlnc-cat-dropdown"
            ref={ddRef}
            className="hlnc__cat-dropdown-portal hlnc__cat-dropdown-portal--open"
            role="listbox"
            style={{ top: ddPos.top, left: ddPos.left, width: ddPos.width, position: 'fixed' }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {Object.entries(categoryVisualMap).map(([name, image]) => (
              <button
                key={name}
                className="hlnc__cat-item"
                role="option"
                aria-selected={form.category === name}
                onClick={() => {
                  if (uiLockedRef.current) return;
                  setForm({ ...form, category: name });
                  setOpenCat(false);
                  setCategoryError(false);
                }}
              >
                <span className="hlnc__cat-item-left">
                  <span className="hlnc__cat-item-icon" style={{ backgroundImage: `url(${image})` }} />
                  <span className="hlnc__cat-item-label">{name}</span>
                </span>
                {form.category === name && (
                  <svg width="16" height="16" viewBox="0 0 24 24" className="hlnc__cat-check" aria-hidden="true">
                    <path
                      d="M5 13l4 4L19 7"
                      fill="none"
                      stroke="#4176FC"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>,
          document.body
        )}

      {colorPickerMounted &&
        createPortal(
          <div
            id="hlnc-color-pop"
            className={`hlnc__colorPopPortal ${colorPickerVisible ? 'is-open' : 'is-closing'}`}
            style={{
              top: colorPickerPos.top,
              left: colorPickerPos.left,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <ColorWheel
              value={textColor}
              onChange={(hex) => setTextColor(hex)}
              onClose={closeColorPicker}
            />
          </div>,
          document.body
        )}
    </div>
  );
}